import supabase from '../supabaseClient';
import { TABLE_NAMES } from '../config';

/** Public invite link base — community + academy player invites (mobile universal links). */
export const COMMUNITY_JOIN_BASE_URL = 'https://www.statbox.co/join';

/** Game + community invite — opens the match in the app and joins both. */
export const GAME_JOIN_BASE_URL = 'https://www.statbox.co/join/game';

/** sessionStorage key — survives OAuth redirect until manager join completes */
export const COMMUNITY_MANAGER_JOIN_STORAGE_KEY = 'statbox:community:managerJoinId';

const COMMUNITY_MANAGER_INVITE_PATH = '/community/invite';
const PRODUCTION_ORIGIN = 'https://www.statbox.co';

/** Native app deep link scheme (matches mobile app.json expo.scheme). */
export const STATBOX_APP_SCHEME = 'com.statbox.app';

/** Shareable invite URL (community id or legacy join token in the path). */
export function buildCommunityJoinUrl(communityIdOrToken) {
  if (!communityIdOrToken) return '';
  return `${COMMUNITY_JOIN_BASE_URL}/${communityIdOrToken}`;
}

/** Opens the Statbox app directly when the user already has it installed. */
export function buildCommunityAppDeepLink(communityIdOrToken) {
  if (!communityIdOrToken) return '';
  return `${STATBOX_APP_SCHEME}://join/${communityIdOrToken}`;
}

export function buildCommunityInviteWhatsAppText(communityName, joinUrl) {
  const name = communityName || 'our community';
  return `Join ${name} on Statbox! Tap this link to join: ${joinUrl}`;
}

/** Shareable link so a player can join this game and the community in the app. */
export function buildGameJoinUrl(matchId) {
  if (!matchId) return '';
  return `${GAME_JOIN_BASE_URL}/${encodeURIComponent(String(matchId).trim())}`;
}

export function buildGameAndCommunityInviteWhatsAppText(communityName, joinUrl) {
  const name = communityName || 'our community';
  return `You're invited to a ${name} game on Statbox. Tap to join the community and this game: ${joinUrl}`;
}

/** Community manager invite path — web sign-in flow for the community platform. */
export function getCommunityManagerJoinBaseUrl() {
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    return `${window.location.origin}${COMMUNITY_MANAGER_INVITE_PATH}`;
  }
  return `${PRODUCTION_ORIGIN}${COMMUNITY_MANAGER_INVITE_PATH}`;
}

export function buildCommunityManagerInviteUrl(communityId) {
  if (!communityId) return '';
  return `${getCommunityManagerJoinBaseUrl()}/${communityId}`;
}

export function buildCommunityManagerInviteWhatsAppText(communityName, joinUrl) {
  const name = communityName || 'our community';
  return `Join ${name} on Statbox! Tap this link to join as a community manager: ${joinUrl}`;
}

export function stashCommunityManagerJoinId(communityId) {
  if (typeof window === 'undefined' || !communityId) return;
  sessionStorage.setItem(COMMUNITY_MANAGER_JOIN_STORAGE_KEY, communityId);
}

export function peekCommunityManagerJoinId() {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(COMMUNITY_MANAGER_JOIN_STORAGE_KEY) || null;
}

export function clearCommunityManagerJoinId() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(COMMUNITY_MANAGER_JOIN_STORAGE_KEY);
}

export function communityManagerInviteSignInPath(communityId) {
  if (!communityId) return '/community/login';
  return `/community/invite/${communityId}/sign-in`;
}

/** Attach manager to community by id (owner invite link flow). */
export async function claimCommunityOwnershipIfUnowned(userId, communityId) {
  if (!userId || !communityId) {
    return { ok: false, error: 'Missing user or community.' };
  }

  const { data: community, error: fetchError } = await supabase
    .from(TABLE_NAMES.COMMUNITIES)
    .select('created_by')
    .eq('id', communityId)
    .maybeSingle();

  if (fetchError) {
    return { ok: false, error: fetchError.message || 'Could not load community.' };
  }
  if (!community || community.created_by) {
    return { ok: true, claimed: false };
  }

  const { error: updateError } = await supabase
    .from(TABLE_NAMES.COMMUNITIES)
    .update({ created_by: userId })
    .eq('id', communityId)
    .is('created_by', null);

  if (updateError) {
    return { ok: false, error: updateError.message || 'Could not assign community owner.' };
  }

  return { ok: true, claimed: true };
}

/** Attach manager to community by id (owner invite link flow). */
export async function joinCommunityAsManager(userId, communityId) {
  if (!userId || !communityId) {
    return { ok: false, error: 'Missing user or community.' };
  }

  const { data: community, error: fetchError } = await supabase
    .from(TABLE_NAMES.COMMUNITIES)
    .select('id, name, created_by')
    .eq('id', communityId)
    .maybeSingle();

  if (fetchError || !community) {
    return { ok: false, error: 'Community not found.' };
  }

  const { error: updateError } = await supabase
    .from(TABLE_NAMES.USERS)
    .update({ community_id: community.id, community_manager: true })
    .eq('user_id', userId);

  if (updateError) {
    return { ok: false, error: 'Could not join community.' };
  }

  const ownership = await claimCommunityOwnershipIfUnowned(userId, communityId);
  if (!ownership.ok) {
    return { ok: false, error: ownership.error || 'Could not assign community owner.' };
  }

  return { ok: true, community, ownerClaimed: ownership.claimed };
}

/** Public preview fields for the manager invite landing + sign-in pages. */
export async function fetchCommunityManagerInvitePreview(communityId) {
  if (!communityId) return null;
  try {
    const { data, error } = await supabase
      .from(TABLE_NAMES.COMMUNITIES)
      .select('id, name, logo_url, sport, is_active')
      .eq('id', communityId)
      .maybeSingle();
    if (error || !data || data.is_active === false) return null;
    return {
      id: data.id,
      name: data.name || 'Community',
      logoUrl: data.logo_url || null,
      sport: String(data.sport || 'Football').trim() || 'Football',
    };
  } catch {
    return null;
  }
}
